/**
 * @fileoverview Chinese translations.
 * @summary Chinese translation strings for KaraoNow frontend.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/i18n/locales/zh
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
export const zh = {
  common: {
    loading: '加载中...',
    processing: '处理中',
    error: '错误',
    success: '成功',
    cancel: '取消',
    confirm: '确认',
    save: '保存',
    delete: '删除',
    edit: '编辑',
    create: '创建',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    search: '搜索',
    filter: '筛选',
    close: '关闭',
    backHome: '返回主页',
    creating: '创建中...',
  },
  auth: {
    login: '登录',
    register: '注册',
    logout: '退出',
    email: '电子邮件',
    password: '密码',
    confirmPassword: '确认密码',
    forgotPassword: '忘记密码？',
    rememberMe: '记住我',
    loginButton: '登录',
    registerButton: '注册',
    alreadyHaveAccount: '已有账户？',
    dontHaveAccount: '没有账户？',
    role: '角色',
    organizer: '组织者',
    singer: '歌手',
    firstName: '名',
    lastName: '姓',
    pseudo: '昵称',
    organization: '组织',
    sendVerificationEmail: '发送验证邮件',
    emailVerificationSent: '验证邮件已发送',
    emailVerificationInstructions: '已向{{email}}发送验证邮件。请点击邮件中的链接完成注册。',
    backToLogin: '返回登录',
    optional: '可选',
    registerOrganizerTitle: '组织者注册',
    registerSingerTitle: '歌手注册',
    registerOrganizerDescription: '创建您的账户以组织卡拉OK活动',
    registerSingerDescription: '创建您的账户以参与卡拉OK活动',
    setPassword: '创建密码',
    setPasswordTitle: '完成注册',
    setPasswordDescription: '创建密码以完成注册',
    createPassword: '创建我的密码',
    passwordRequirements: '至少12个字符，包含大写字母、小写字母、数字和特殊字符',
    forgotPasswordDescription: '输入您的电子邮件地址，我们将向您发送重置密码的链接。',
    sendResetLink: '发送重置链接',
    resetPasswordEmailSent: '密码重置邮件已发送',
    resetPasswordInstructions: '已向{{email}}发送密码重置邮件。请点击邮件中的链接重置您的密码。',
    resetPassword: '重置密码',
    resetPasswordDescription: '为您的账户创建新密码',
    resetPasswordButton: '重置我的密码',
    passwordResetSuccess: '密码已重置',
    passwordResetSuccessDescription: '您的密码已成功重置。您将被重定向到登录页面。',
    resetLinkExpired: '重置链接已过期',
    resetLinkExpiredDescription: '此重置链接已过期（有效期为1小时）。请请求新链接。',
    requestNewResetLink: '请求新链接',
    rememberPassword: '还记得您的密码？',
  },
  // Organizer menu
  organizerMenu: {
    menu: '组织者菜单',
    dashboard: '仪表板',
    venues: '场地',
    settings: '设置',
    dataPrivacy: '个人数据',
    subscriptions: '价格',
    user: '用户',
  },
  errors: {
    genericMessage: '发生错误',
    required: '此字段为必填项',
    invalidEmail: '无效的电子邮件',
    passwordTooShort: '密码必须至少包含12个字符',
    passwordRequirements: '密码必须包含至少一个大写字母、一个小写字母、一个数字和一个特殊字符',
    passwordsDoNotMatch: '密码不匹配',
    loginFailed: '登录失败',
    registerFailed: '注册失败',
    resetPasswordFailed: '密码重置失败',
    invalidToken: '无效或过期的令牌',
    networkError: '网络错误',
    unauthorized: '未授权',
    forbidden: '访问被拒绝',
    notFound: {
      title: '页面未找到',
      description: '您查找的页面不存在或已被移动。',
      explore: '探索应用',
    },
    server: {
      title: '服务器错误',
      description: '发生意外错误。请稍后再试。',
    },
    generic: {
      title: '哎呀，出了点问题',
      description: '发生意外错误。请重试。',
    },
    serverError: '服务器错误',
    // Error codes from backend
    VALIDATION_ERROR: '验证错误',
    CREATE_EVENT_ERROR: '创建活动时出错',
    UPDATE_EVENT_ERROR: '更新活动时出错',
    EVENT_NOT_FOUND: '未找到活动',
    VENUE_NOT_FOUND: '未找到地点',
    QUOTA_EXCEEDED_CREATE: '已达到活动创建的月度配额',
    QUOTA_EXCEEDED_DATE_CHANGE: '{{planName}}计划的月度配额已满：每月最多{{limit}}个活动。您的配额将在计费周期结束时重置。',
    SUBSCRIPTION_EVENT_CANNOT_CONVERT: '订阅计划中包含的活动无法转换为单个活动',
    EVENT_DATE_NOT_REACHED: '无法在预定日期之前激活活动',
    EVENT_DATE_PAST: '活动日期不能是过去的日期',
    EVENT_DATE_AFTER_EXPIRATION: '活动日期不能晚于您唯一活动的到期日期',
    // Validation errors by field and Zod code
    validation: {
      name: {
        too_small: '活动名称为必填项',
        required: '活动名称为必填项',
        invalid_type: '活动名称无效',
      },
      date: {
        required: '日期为必填项',
        invalid_type: '日期无效',
        invalid_date: '日期无效',
        invalid_string: '日期格式无效',
      },
      description: {
        invalid_type: '描述无效',
      },
      venueId: {
        invalid_type: '地点无效',
      },
    },
  },
  // Landing page
  landing: {
    languageLabel: '选择您的语言',
    heroTitle: '您的卡拉OK之夜，更简单。',
    heroSubtitle: '2分钟创建活动。嘉宾扫码添加歌曲 — 您来掌控整场节奏。',
    ctaPrimary: '创建我的第一个卡拉OK — 免费',
    ctaOrganize: '我想组织',
    ctaOrganizeDesc: '创建活动、管理歌单、跟进演唱顺序。',
    ctaSing: '我想唱歌',
    ctaSingDesc: '通过二维码加入活动并添加您的歌曲。',
    alreadyAccount: '已有账号？',
    login: '登录',
    whyTitle: '为什么选择KaraoNow？',
    benefit1Title: '一个二维码，零纸张',
    benefit1Desc: '嘉宾扫码用手机选歌。再也不怕名单弄丢。',
    benefit2Title: '实时歌单',
    benefit2Desc: '演唱顺序人人可见。轻松控场，不用手忙脚乱。',
    benefit3Title: '免费起步',
    benefit3Desc: '最多4场免费活动。随时可升级为不限次数。',
    twoWaysTitle: 'KaraoNow的两种用法',
    learnMore: '了解更多',
  },
  home: {
    title: '卡拉OK活动播放列表管理',
    wantToSing: '我想唱歌',
    wantToSingDescription: '参加活动并添加您的歌曲',
    wantToOrganize: '我想组织',
    wantToOrganizeDescription: '创建和管理您的卡拉OK活动',
    alreadyHaveAccount: '已有账户？',
  },
  dashboard: {
    title: '组织者仪表板',
    welcome: '欢迎',
    events: '活动',
    createEvent: '创建活动',
    noEvents: '没有活动',
    noEventsDescription: '开始创建您的第一个卡拉OK活动',
    filterAll: '全部',
    filterDraft: '草稿',
    filterActive: '进行中',
    filterCompleted: '已完成',
    filterCancelled: '已取消',
    loadingEvents: '加载活动中...',
    errorLoadingEvents: '加载活动时出错',
    noEventsMatchingFilter: '没有符合此筛选条件的活动',
    tryDifferentFilter: '请尝试选择不同的筛选条件',
  },
  events: {
    name: '活动',
    events: '活动',
    description: '描述',
    date: '日期',
    time: '时间',
    venue: '地点',
    selectVenue: '选择地点',
    namePlaceholder: '我的卡拉OK之夜',
    descriptionPlaceholder: '可选描述...',
    createTitle: '创建活动',
    createSubtitle: '创建新的卡拉OK活动',
    createButton: '创建活动',
    createEvent: '创建活动',
    createSuccess: '活动创建成功',
    createError: '创建活动时出错',
    noVenues: '没有可用的地点。请先创建地点。',
    createVenue: '创建地点',
    status: '状态',
    statusDraft: '草稿',
    statusActive: '进行中',
    statusCompleted: '已完成',
    statusCancelled: '已取消',
    statusHidden: '已隐藏',
    createdAt: '创建于',
    updatedAt: '更新于',
    view: '查看',
    open: '打开',
    edit: '编辑',
    delete: '删除',
    qrCode: '二维码',
    deleteConfirmTitle: '删除活动',
    deleteConfirm: '您确定要删除活动 "{{name}}" 吗？',
    deleteSuccess: '活动已成功删除',
    deleteError: '删除活动时出错',
    managementTitle: '活动管理',
    editEvent: '编辑活动',
    updateSuccess: '活动更新成功',
    updateError: '更新活动时出错',
    showQRCode: '显示二维码',
    hideQRCode: '隐藏二维码',
    qrCodeTitle: '活动二维码',
    qrCodeDescription: '与参与者分享此代码，让他们可以加入活动',
    copyQRCode: '复制链接',
    qrCodeCopied: '链接已复制到剪贴板',
    shareQRCode: '分享',
    queue: '队列',
    singers: '歌手',
    noQueue: '队列中没有歌曲',
    noSingers: '没有注册的歌手',
    guest: '访客',
    singer: '歌手',
    songsCount_one: '{{count}}首歌曲',
    songsCount_other: '{{count}}首歌曲',
    deleteQueueItem: '删除歌曲',
    deleteQueueItemConfirm: '您确定要从队列中删除这首歌曲吗？',
    deleteSinger: '删除歌手',
    deleteSingerConfirm: '您确定要删除这位歌手吗？队列中的所有歌曲也将被删除。',
    singerDeleteError: '删除歌手时出错',
    moveUp: '上移',
    moveDown: '下移',
    dragToReorder: '拖动以重新排序',
    setCurrent: '设为当前',
    markCompleted: '标记为已完成',
    cancel: '取消',
    queueUpdateError: '更新队列时出错',
    queueDeleteError: '删除歌曲时出错',
    queueStatusPending: '等待中',
    queueStatusCurrent: '当前',
    queueStatusCompleted: '已完成',
    queueStatusCancelled: '已取消',
    qrCodeUrl: '活动URL',
    qrCodeShareText: '加入这个卡拉OK活动：{{eventName}}！',
    qrCodePrintTitle: '二维码 - KaraoNow',
    qrCodePrintInstructions: '扫描此二维码以访问活动并将您的歌曲添加到队列中。',
    qrCodePrint: '打印',
    qrCodeShare: '分享',
    qrCodeDownload: '下载',
    startEvent: '启动活动',
    start: '启动',
    completeEvent: '完成活动',
    complete: '完成',
    cancelEvent: '取消活动',
    cancelConfirmTitle: '确认取消',
    cancelConfirmMessage: '您确定要取消此活动吗？此操作不可撤销。',
    cancelConfirmOneShotTitle: '确认取消 - 一次性活动',
    cancelConfirmOneShotMessage: '⚠️ 警告：此一次性活动已购买。取消是最终决定，如果活动未被使用，将不会退款。您确定要取消此活动吗？',
    cancelConfirmButton: '是的，取消',
    cancelCancelButton: '不，保留活动',
    deleteEvent: '删除活动',
    deleteEventConfirm: '确定要删除此活动吗？此操作无法撤销。',
    hide: '隐藏',
    hideConfirmTitle: '隐藏活动',
    hideConfirm: '隐藏活动 "{{name}}" ？它将不再显示在您的主列表中，但仍会计入您的月度配额。',
    hideSuccess: '活动已成功隐藏',
    hideError: '隐藏活动时出错',
    hidden: '已隐藏',
    showHidden: '已隐藏',
    hiddenEventTitle: '隐藏的活动',
    hiddenEventMessage: '此活动已隐藏，无法再访问。它仍会计入您的月度配额。',
    hiddenEventTooltip: '此活动已隐藏，无法查看',
    statusUpdated: '活动状态已更新',
    backToEvent: '返回活动',
    // Quota exceeded for date change
    quotaExceededDateChange: '此日期的月度配额已满',
    quotaExceededDateChangeTitle: '月度配额已满',
    quotaExceededDateChangeExplanation: '您无法更改活动"{{eventName}}"的日期，因为这会超过您的月度活动配额。',
    quotaExceededDateChangeOptions: '您可以保持原始日期，或将此活动转换为"单个活动"以将其从月度配额中移除。',
    quotaExceededDetails: '此期间您有{{current}} / {{limit}}个活动。',
    upgradeToOneShotOption: '选项：升级为单个活动',
    upgradeToOneShotDescription: '通过将此活动转换为"单个活动"，它将不再计入您的月度配额，您可以自由更改其日期。',
    upgradeToOneShotNow: '🚀 升级为单个活动',
    keepOriginalDate: '保持原始日期',
    multipleEventsPremiumSuggestion: '多个活动？高级计划可能更具成本效益。',
    // Quota exceeded for event creation
    quotaExceededCreateTitle: '月度配额已满',
    quotaExceededCreateMessage: '您已达到{{planName}}的限制。您无法在此期间创建更多活动。',
    quotaExceededCreateExplanation: '您的月度活动配额已满。您可以等待计费周期结束，或升级到更高级的计划。',
    quotaExceededCreateOptions: '您也可以创建一个单个活动，它不会计入您的月度配额。',
    // Quota exceeded for FREE plan (no monthly/billing period references)
    quotaExceededCreateTitleFree: '配额已满',
    quotaExceededCreateMessageFree: '您已达到{{planName}}的限制。您无法创建更多活动。',
    quotaExceededCreateExplanationFree: '您已达到免费计划的活动限制。您可以升级到更高级的计划以创建更多活动。',
    quotaExceededCreateOptionsFree: '您也可以创建一个单个活动，它不会计入您的配额。',
    quotaExceededDetailsFree: '您有{{current}} / {{limit}}个活动。',
    createOneShotDescriptionFree: '创建一个不会计入您配额的单个活动。',
    upgradePlanDescriptionFree: '升级到更高级的计划以增加您的活动配额。',
    createOneShotOption: '选项：创建单个活动',
    createOneShotDescription: '创建一个不会计入您月度配额的单个活动。',
    createOneShotNow: '🚀 创建单个活动',
    upgradePlanOption: '选项：升级到更高级的计划',
    upgradePlanDescription: '升级到更高级的计划以增加您的月度活动配额。',
    upgradePlanNow: '升级到更高级的计划',
    quotaResetInfo: '您的配额将在计费周期结束时重置。',
    // Generic quota exceeded message
    quotaExceeded: '此日期的月度配额已满',
    // Plan subscription badge
    planSubscription: '包含在计划中',
    planSubscriptionTooltip: '包含在您的订阅中的活动',
  },
  // Venues
  venues: {
    title: '地点管理',
    subtitle: '创建和管理您的卡拉OK地点',
    venuesList: '地点列表',
    addVenue: '添加地点',
    editVenue: '编辑地点',
    noVenues: '没有注册的地点',
    noVenuesDescription: '开始创建您的第一个地点',
    eventName: '活动名称',
    eventNamePlaceholder: '{{firstName}}的卡拉OK',
    name: '地点名称',
    namePlaceholder: '例如：黑猫酒吧',
    address: '地址',
    addressPlaceholder: '例如：音乐街123号',
    city: '城市',
    cityPlaceholder: '例如：北京',
    country: '国家',
    countryPlaceholder: '例如：中国',
    googleMapsUrl: 'Google地图链接',
    googleMapsUrlPlaceholder: 'https://maps.google.com/...',
    viewOnGoogleMaps: '在Google地图上查看',
    addSuccess: '地点创建成功',
    addError: '创建地点时出错',
    updateSuccess: '地点更新成功',
    updateError: '更新地点时出错',
    deleteSuccess: '地点删除成功',
    deleteError: '删除地点时出错',
    confirmDelete: '您确定要删除地点"{{name}}"吗？',
    confirmDeleteTitle: '删除地点',
    cancel: '取消',
    add: '添加',
    edit: '编辑',
    save: '保存',
    deleting: '删除中...',
    saving: '保存中...',
    generateMapsUrl: '生成 Google Maps 链接',
    generateMapsUrlError: '请至少填写场地名称或城市以生成链接',
    mapsModal: {
      title: '在 Google Maps 上选择位置',
      description: '使用 Google Maps 查找并精确调整您的场地位置。',
      instructions: '点击下方的"在 Google Maps 中打开"，如果需要，调整地图上的点，然后从浏览器地址栏复制链接并将其粘贴到下面的字段中。',
      urlLabel: 'Google Maps 链接',
      openInMaps: '在 Google Maps 中打开',
      confirm: '确认',
      clickToOpen: '点击打开 Google Maps',
      clickDescription: '将打开一个新窗口显示 Google Maps，以便您可以调整位置',
    },
  },
  // Singer interface
  singer: {
    // Dashboard
    dashboard: '仪表板',
    dashboardSubtitle: '查看您的活动和歌曲',
    myEvents: '我的活动',
    mySongs: '我的歌曲',
    allEvents: '全部',
    activeEvents: '活跃',
    pastEvents: '过去',
    inactiveEvents: '非活跃',
    sortByDate: '按日期排序',
    sortByOrganizer: '按组织者排序',
    sortBySongs: '按歌曲数排序',
    noEvents: '您还没有参加任何活动。',
    noSongs: '您还没有添加任何歌曲。',
    eventName: '活动名称',
    organizer: '组织者',
    date: '日期',
    songsAdded: '添加的歌曲',
    songsSung: '演唱的歌曲',
    status: '状态',
    status_active: '活跃',
    status_completed: '已完成',
    status_cancelled: '已取消',
    status_draft: '草稿',
    title: '标题',
    artist: '艺术家',
    event: '活动',
    addedDate: '添加日期',
    totalEvents: '活动总数',
    totalSongs: '歌曲总数',
    uniqueOrganizers: '独特的组织者',
    // Settings
    settings: '设置',
    settingsSubtitle: '管理您的个人资料和数据',
    menu: '歌手菜单',
    profile: '个人资料',
    changePassword: '更改密码',
    changePasswordDescription: '我们将通过电子邮件向您发送重置密码的链接。',
    sendResetLink: '发送重置链接',
    passwordResetLinkSent: '密码重置链接已发送到您的电子邮件地址。',
    accountData: '帐户数据',
    editProfile: '编辑我的个人资料',
    saveChanges: '保存更改',
    profileUpdated: '个人资料已成功更新',
    changePasswordTitle: '更改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmNewPassword: '确认新密码',
    passwordRequirements: '密码必须至少12个字符，包含大写字母、小写字母、数字和特殊字符。',
    passwordChanged: '密码已成功更改',
    passwordTooShort: '密码必须至少12个字符',
    passwordsNotMatch: '密码不匹配',
    currentPasswordRequired: '当前密码为必填项',
    // 密码验证错误
    passwordMinLength: '密码必须至少包含12个字符',
    passwordNoUppercase: '密码必须包含至少一个大写字母',
    passwordNoLowercase: '密码必须包含至少一个小写字母',
    passwordNoDigit: '密码必须包含至少一个数字',
    passwordNoSpecialChar: '密码必须包含至少一个特殊字符',
    exportData: '导出我的数据',
    exportDataDescription: '以JSON格式下载所有个人数据的副本。',
    downloadData: '下载我的数据',
    dataExported: '数据已成功导出',
    dangerZone: '危险区域',
    deleteAccountDescription: '删除您的帐户是不可逆的。所有数据将被永久删除。',
    deleteAccount: '删除我的帐户',
    deleteAccountConfirm: '删除',
    confirmDeleteAccount: '确定要删除您的帐户吗？此操作是不可逆的。',
    // QR Code Landing
    qrCodeMissing: '缺少二维码令牌',
    qrCodeError: '验证二维码时出错',
    eventNotFound: '未找到活动或令牌无效',
    organizerNotAllowed: '您是组织者。请使用您的控制台管理此活动。',
    accessDenied: '访问被拒绝',
    validatingQRCode: '正在验证二维码...',
    chooseAccessMethod: '选择如何加入活动',
    continueAsGuest: '以访客身份继续',
    orWithAccount: '或使用账户',
    enterPseudo: '输入您的昵称以加入活动',
    joinEvent: '加入活动',
    joining: '连接中...',
    sessionError: '创建会话时出错',
    joinError: '加入活动时出错',
    sessionExpired: '您的会话已过期。请重新扫描活动二维码。',
    guestMode: '访客模式',
    leaveEvent: '离开活动',
    // Singer Event Page
    eventNotActive: '此活动尚未激活。请稍后再来！',
    eventNotStarted: '此活动尚未开始。组织者启动卡拉OK后将可用！',
    eventNotYetAvailable: '此活动尚不可用。请等到预定日期。',
    eventEnded: '此活动已结束。不再接受投票。',
    eventCancelled: '此活动已被取消。投票不可用。',
    searchSong: '搜索歌曲',
    searchResults: '搜索结果',
    advancedSearch: '高级搜索',
    advancedSearchToggle: '高级搜索',
    simpleSearch: '简单搜索',
    searchPlaceholder: '标题或艺术家...',
    titlePlaceholder: '标题...',
    artistPlaceholder: '艺术家...',
    noResults: '未找到结果',
    addCustomSong: '添加此曲目',
    addCustomSongDescription: '此曲目将保存到数据库并加入您的队列。',
    addAndQueue: '添加并加入队列',
    localResults: '在我们的数据库中找到{{count}}个结果',
    musicbrainzResults: '{{count}}个MusicBrainz结果',
    moreResults: '更多结果',
    addError: '添加到队列时出错',
    queueLimitReached: '已达到限制：此活动的组织者正在使用免费计划，该计划限制队列中最多{{limit}}首歌曲。队列目前包含{{currentCount}}首歌曲。如果您想添加更多歌曲，请联系组织者。',
    songAdded: '「{{title}}」已添加到队列！',
    myQueue: '我的歌曲',
    noSongsInQueue: '您还没有添加歌曲',
    fullQueue: '队列',
    queueEmpty: '队列为空',
    you: '您',
    unknown: '未知',
    // Queue statuses
    statusPending: '等待中',
    statusCurrent: '播放中',
    statusCompleted: '已完成',
    statusCancelled: '已取消',
  },
  // Subscription and Plans
  subscription: {
    title: '计划和定价',
    subtitle: '选择适合您需求的计划',
    currentPlan: '当前计划',
    planFree: '免费计划',
    planOneShot: '单个活动',
    planPro: '专业计划',
    planPremium: '高级计划',
    planUltimate: '终极计划',
    planFreeDescription: '完美的入门级',
    planOneShotDescription: '用于一个活动',
    planProDescription: '适合定期组织者',
    planPremiumDescription: '为爱好者提供',
    planUltimateDescription: '为专业人士提供',
    priceFreeFree: '免费',
    priceOneShotPer: '{{price}}€ / 活动',
    priceProPer: '{{price}}€ / 月',
    pricePremiumPer: '{{price}}€ / 月',
    priceUltimatePer: '{{price}}€ / 月',
    maxEvents: '最大活动数',
    maxSongsPerEvent: '每个活动的歌曲',
    unlimited: '无限制',
    features: '功能',
    currentPlanBadge: '当前计划',
    changePlan: '更改计划',
    upgradePlan: '升级到高级',
    upgradePlanButton: '升级到更高计划',
    selectPlan: '选择此计划',
    monthlyQuota: '月度配额',
    freeQuota: '免费配额',
    eventsUsed: '{{current}} / {{limit}} 活动',
    billingPeriod: '期间: {{start}} - {{end}}',
    quotaReached: '月度配额已满',
    quotaReachedMessage: '您已达到此期间的活动限制。配额将在计费周期结束时重置。',
    quotaWarning: '月度配额即将满',
    quotaWarningMessage: '此期间您还有 {{remaining}} 个活动。',
    quotaInfo: '此期间您还有 {{remaining}} 个活动。',
    learnMore: '了解更多',
    contactSupport: '联系支持',
    freePlanNote: '免费计划限制为{{maxEvents}}个活动（所有状态，不包括单个活动）和{{maxSongsPerEvent}}首歌曲/活动。',
    oneShotPlanNote: '活动播放列表中的无限歌曲数量。有效期为15天。',
    proPlanNote: '{{maxEvents}}个活动/月（滚动期间）。无限歌曲。',
    premiumPlanNote: '{{maxEvents}}个活动/月（滚动期间）。无限歌曲。',
    ultimatePlanNote: '无限活动和歌曲。',
    noEngagement: '无承诺。',
    convertEvent: '转换活动',
    createEvent: '创建活动',
    chooseOption: '选择一个选项:',
    convertDescription: '选择一个免费活动并将其转换为单个活动计划。',
    createDescription: '使用单个活动计划创建新活动。',
    noFreeEventsToConvert: '您没有可用的免费活动来转换。',
    selectFreeEvent: '选择要转换的免费活动:',
    convertThisEvent: '转换此活动',
    createNewOneShot: '创建单个活动',
    newOneShotEventName: '新的单个活动',
    createEventError: '创建活动时出错',
    createOneShotEventTitle: '创建单个活动',
    proceedToPayment: '继续付款',
    // Stripe checkout
    checkoutError: '支付期间发生错误',
    paymentSuccessful: '支付成功!',
    paymentProcessing: '您的支付正在处理中。您将很快被重定向。',
    redirectingIn5: '5秒内自动重定向...',
    backToSubscription: '返回计划',
    successTitle: '支付已确认',
    successSubtitle: '感谢您的购买',
    paymentCancelled: '支付已取消',
    checkoutCancelledMessage: '您取消了支付。您可以随时重试。',
    cancelledTitle: '支付已取消',
    cancelledSubtitle: '返回计划以重试',
    whatHappensNext: '接下来会发生什么?',
    webhooksProcessing: '我们正在处理您的支付',
    subscriptionActive: '您的订阅将很快激活',
    confirmationEmail: '您将收到确认电子邮件',
    // Subscription cancellation
    cancelConfirm: '您确定要取消订阅吗?',
    cancelDetails: '您将保持对Premium计划的访问权限,直到当前计费周期结束。之后,您将被降级为免费计划。',
    cancelSuccess: '您的订阅已成功取消。',
    cancelPlan: '取消',
    cancellationInProgress: '您的订阅取消已在进行中。您将在计费周期结束时切换到免费计划。',
    cancellationInProgressTitle: '取消进行中',
    cancellationInProgressMessage: '您的订阅取消已在进行中。您将在当前计费周期结束时切换到免费计划。无需进一步操作。',
    accessUntil: '您将可以访问到{{date}}。',
    cancelError: '取消订阅时出错',
    cancelModalTitle: '取消订阅',
    cancelModalMessage: '您将保留对高级计划的访问权限，直到当前账单周期结束。之后，您将自动切换到免费计划。',
    cancelModalConfirm: '是的，取消我的订阅',
    endOfBillingPeriod: '账单周期结束',
    // ONE_SHOT upgrade
    upgradeToOneShot: '升级到单个活动',
    upgradeOneShotConfirm: '您即将将活动"{{eventName}}"升级到单个活动计划。',
    oneShotDescription: '这是针对此特定活动的无限歌曲的一次性付款。',
    price: '价格',
    oneTimePayment: '一次性付款',
    whatYouGet: '您将获得:',
    unlimitedSongs: '此活动的无限歌曲',
    noLimits: '无队列限制',
    oneShotValidity: '活动结束前有效',
    upgradeNow: '立即升级',
    upgradeError: '升级活动时出错',
    updateSuccess: '订阅已成功更新为{{plan}}计划',
    changePlanTitle: '更改计划',
    changePlanSubtitle: '您即将修改您的订阅',
    newPlan: '新计划',
    perMonth: '月',
    upgradeCost: '额外费用: +{{amount}}€ / 月',
    downgradeCredit: '预估信用: -{{amount}}€ / 月',
    immediateChange: '立即更改',
    immediateChangeDescription: '计划更改将立即生效。按比例计费将由Stripe自动计算。',
    confirmChange: '确认更改',
    oneShot: '单次活动',
    oneShotTooltip: '单次活动订阅',
    expiresLabel: '到期日期',
    oneShotExpirationTitle: '您的单次活动到期',
    oneShotDaysRemaining: '剩余{{count}}天',
    oneShotDaysRemaining_plural: '剩余{{count}}天',
    oneShotExpiresOn: '到期日期：{{date}}',
    oneShotExpirationWarning: '⚠️ 此日期后，您的活动将恢复为免费计划。请务必在到期前使用！',
    oneShotValidityInfo: '此购买自购买日起{{days}}天内有效。请在到期前使用！',
    upgradeToUnlimited: '🚀 升级到无限制',
    upgradeToUnlimitedNow: '🚀 立即升级到无限制',
    upgradeSingleEventConfirm: '您即将将活动"{{eventName}}"升级为无限制。',
    singleEventDescription: '这是一次性付款，可在此特定活动中使用无限歌曲。',
    singleEventPlan: '单次活动',
    multiplePremiumSuggestion: '多个活动？高级套餐可能更划算。',
    comparePlans: '比较套餐 →',
    premium: '高级',
  },
  // Plan alerts
  planAlert: {
    limitReached: '已达到计划限制',
    songsLimitReached: '您已达到免费计划限制({{limit}}首歌曲)。要添加更多歌曲,请订阅付费计划。',
    eventsLimitReached: '您已达到免费计划限制({{limit}}个活动)。要创建更多活动,请订阅付费计划。',
    upgrade: '订阅',
    dismissible: true,
  },
  pwa: {
    install: '安装',
    installApp: '安装应用',
    installPrompt: '安装 KaraoNow 以便快速访问！',
    installed: '应用已安装',
    offline: '您处于离线状态',
    offlineMessage: '某些功能可能受限。',
    updateAvailable: '有可用更新',
    updateNow: '立即更新',
  },
  // About
  about: {
    title: '关于 KaraoNow',
    subtitle: '发现改变您卡拉OK活动的应用程序',
    introduction: 'KaraoNow 是什么？',
    introductionText: 'KaraoNow 是一个创新的 SaaS 平台，旨在以无缝和专业的方式管理您的卡拉OK活动。无论您是活动组织者还是充满热情的歌手，KaraoNow 都为您提供创建难忘的卡拉OK体验所需的工具。',
    missionText: '我们的使命是简化卡拉OK播放列表管理，并在组织者和歌手之间建立真实的联系，使每个活动都成为难忘且组织完善的体验。',
    features: '主要功能',
    feature1Title: '事件管理',
    feature1Desc: '使用所有必要的详细信息轻松创建和管理您的卡拉OK活动。',
    feature2Title: '互动参与',
    feature2Desc: '允许歌手通过二维码加入您的活动并投票支持他们最喜欢的歌曲。',
    feature3Title: '实时播放列表',
    feature3Desc: '实时查看和管理歌曲队列，获得即时更新。',
    feature4Title: '详细统计信息',
    feature4Desc: '通过图表和实时指标分析您的活动表现。',
    feature5Title: '增强的安全性',
    feature5Desc: '您的数据受到高安全标准和强大的云基础设施的保护。',
    feature6Title: '多语言支持',
    feature6Desc: 'KaraoNow 支持 8 种语言以覆盖全球受众。',
    technology: '技术栈',
    frontend: '前端',
    backend: '后端',
    version: '当前版本',
    versionNote: '定期更新新功能',
    visitWebsite: '官方网站',
    support: '支持',
    supportNote: '欢迎联系我们解答任何问题',
    copyright: '版权所有。',
    privacyNotice: '请查看我们的法律页面了解更多关于数据保护的信息。',
    tryForFree: '开始您的免费试用',
  },
  // Organizer
  organizer: {
    // Dashboard
    dashboard: '仪表板',
    dashboardSubtitle: '管理您的卡拉OK活动',
    menu: '组织者菜单',
    // Settings
    settings: '设置',
    settingsSubtitle: '管理您的资料和设置',
    editProfile: '编辑我的资料',
    saveChanges: '保存更改',
    profileUpdated: '资料已成功更新',
    emailChangeConfirmTitle: '修改电子邮件地址',
    emailChangeConfirmMessage: '确认电子邮件将发送到您的新电子邮件地址。您的新电子邮件只有在您验证该电子邮件中的链接后才会生效。您的旧电子邮件在验证之前仍然有效。',
    changePassword: '更改密码',
    changePasswordDescription: '我们会通过电子邮件向您发送重置密码的链接。',
    sendResetLink: '发送重置链接',
    passwordResetLinkSent: '密码重置链接已发送到您的电子邮件。',
    accountData: '帐户数据',
    exportData: '导出我的数据',
    exportDataDescription: '以JSON格式下载所有个人数据的副本。',
    downloadData: '下载我的数据',
    dataExported: '数据导出成功',
    dangerZone: '危险区域',
    deleteAccountDescription: '删除您的帐户是不可逆的。您的所有数据将被永久删除。',
    deleteAccount: '删除我的帐户',
    deleteAccountConfirm: '删除',
    confirmDeleteAccount: '您确定要删除您的帐户吗?此操作不可逆。',
    // Plan section
    planSection: '我的订阅',
    planSectionDescription: '查看您当前的计划、限制和升级选项',
    renewDate: '续订日期: ',
    viewPlanDetails: '查看计划详情',
    eventsLimit: '创建的活动（不包括独特活动）',
    subscriptionStatus: '状态',
    statusActive: '活跃',
    statusInactive: '非活跃',
    statusScheduledEnd: '计划结束',
    subscriptionExpires: '过期日期',
    subscriptionRenewsOn: '续订日期',
    cancellationConfirmed: '取消已确认！',
    continueAccessUntil: '您将继续享受高级计划直到{{date}}',
    autoDowngradeToFree: '在此日期之后，您将自动切换到免费计划。',
    accessUntilEndOfPeriod: '您将保留访问权限直到当前账单周期结束。',
    planFeatures: '计划功能',
    featureEvents: '每月事件',
    featureSongsPerEvent: '每个事件的歌曲',
    unlimited: '无限制',
    upgradePlan: '升级我的计划',
    managePlan: '管理我的订阅',
  },
  // Email change
  email: {
    changeEmail: '更改我的电子邮件地址',
    changeEmailDescription: '要更改您的电子邮件地址，您必须首先确认新地址。确认电子邮件将发送到新地址。',
    currentEmail: '当前电子邮件',
    newEmail: '新电子邮件地址',
    newEmailAddress: '新地址',
    requestEmailChange: '请求更改电子邮件',
    emailRequired: '电子邮件地址是必需的',
    emailUnchanged: '新电子邮件地址必须与当前地址不同',
    emailChangeConfirmationSent: '确认电子邮件已发送到您的新电子邮件地址。',
    emailChangeInfo: '在新地址被验证之前，您的旧电子邮件将保持活跃。',
    verifying: '正在验证',
    verifyingDescription: '正在验证您的电子邮件地址...',
    success: '电子邮件已确认',
    confirmationSuccess: '您的电子邮件地址已成功更改!',
    confirmationFailed: '确认失败',
    linkExpired: '链接已过期',
    tokenExpired: '确认链接已过期。请请求新链接。',
    linkExpiredDescription: '确认链接已过期(有效期24小时)。请请求新的确认链接。',
    redirecting: '正在重定向...',
    redirectingToLogin: '3秒后重定向到登录...',
    goToSettings: '转到设置',
    backToSettings: '返回设置',
    requestNewLink: '请求新链接',
    missingToken: '缺少确认令牌。',
    validationPending: '验证待批',
    pendingEmailAddress: '等待验证',
  },
  // RGPD - 数据隐私
  rgpd: {
    pageTitle: '个人数据',
    pageSubtitle: '安全地管理您的数据和帐户',
    exportData: '下载我的数据',
    exportDataDescription: '以JSON格式下载您的个人数据的完整副本。您可以使用这些数据将其导入其他地方或简单地将其保留作为备份。',
    exportIncludes: '此导出包括',
    exportProfile: '配置文件和偏好',
    exportHistory: '事件历史',
    exportEvents: '创建的事件',
    exportSubscription: '订阅信息',
    downloadData: '下载（JSON）',
    exporting: '下载中...',
    exportFormatJSON: '格式：JSON（符合GDPR）',
    dataExportedSuccess: '✅ 您的数据已成功下载！',
    deleteAccount: '删除我的帐户',
    deleteAccountDescription: '此操作将永久删除您的帐户和所有关联数据。此操作无法撤消。',
    deleteWarning: '⚠️ 重要警告',
    deleteIrreversible: '此操作是永久性的且不可逆的',
    deleteAllData: '您的所有数据将被删除',
    deleteNoRecovery: '没有恢复的可能性',
    deleteRecommendation: '我们建议在删除帐户前下载您的数据。',
    deleteAccountButton: '删除我的帐户',
    confirmDeletion: '确认删除',
    deleteConfirmMessage: '您确定要删除您的帐户吗？此操作无法撤消。',
    continueDelete: '继续删除',
    finalDeleteWarning: '最终确认 - 无法返回',
    finalDeleteMessage: '这是您的最后机会！点击"永久删除"将不可逆地删除您的帐户。',
    permanentlyDelete: '永久删除',
    gdprCompliance: 'GDPR合规性',
    gdprInfo: '根据GDPR法规，您有权访问、更正、删除和移植您的个人数据。这些工具使您能够行使这些权利。',
  },
  // Cookies consent
  cookies: {
    title: '🍪 我们使用cookie',
    description: '我们使用cookie来改善您的体验。有关更多信息，请查看我们的政策。',
    understood: '明白了',
    learnMore: '了解更多',
    footerNote: '您可以查看我们的Cookie政策了解更多详情。',
  },
  // Cookies policy page
  cookiesPage: {
    title: 'Cookie政策',
    subtitle: '有关KaraoNow使用的cookie的完整信息',
    overview: '概述',
    overviewText: 'KaraoNow使用cookie来增强您的用户体验，保护您的帐户并了解您如何使用我们的平台。此政策解释了我们如何以及为什么使用cookie。',
    whatAreCookies: '什么是Cookie？',
    whatAreCookiesText: 'Cookie是存储在您设备上的小型文本文件。它们允许网站记住有关您访问的信息，例如您的偏好和登录数据。',
    typesOfCookies: 'Cookie类型',
    sessionCookies: '会话Cookie',
    sessionCookiesText: '这些cookie在您关闭浏览器时过期。它们用于保持您的会话活跃并保护您的访问。',
    persistentCookies: '持久Cookie',
    persistentCookiesText: '这些cookie在您的设备上保存定义的期间。它们允许我们在您下次访问时认识您。',
    howWeUseCookies: '我们如何使用Cookie',
    useAuthentication: '身份验证和会话安全',
    usePreferences: '记住您的偏好 (语言、主题)',
    useSecurity: '防止滥用和攻击',
    usePerformance: '提高平台性能',
    yourRights: '您的权利',
    yourRightsText: '您有权控制cookie。您可以通过我们的同意横幅接受或拒绝非必需的cookie。',
    managingCookies: '管理Cookie',
    managingCookiesText: '您可以通过浏览器设置管理cookie。请注意，禁用某些cookie可能会影响KaraoNow的功能。',
    contact: '联系方式',
    contactText: '如有任何关于此政策的问题，请通过我们的联系页面或服务条款与我们联系。',
    footer: '有疑问吗？返回首页。',
  },
  // Singer menu
  singerMenu: {
    about: '关于',
    dataPrivacy: '个人数据',
  },
  // Footer
  footer: {
    legal: '法律声明',
    privacy: '隐私政策',
    cookies: '饼干政策',
    terms: '条款和条件',
  },
  // 法律声明页面
  legalPage: {
    title: '法律声明',
    subtitle: '关于 KaraoNow 的法律信息',
    editor: {
      title: '网站编辑',
      name: 'axialdata',
      address: 'Impasse du pélican, 97233 Schoelcher, 马提尼克',
      phone: '电话: +596696367741',
      email: '电子邮件: contact@axialdata.net',
      siret: 'SIRET: 49347625300023',
    },
    publication: {
      title: '出版负责人',
      director: 'Pierre-Yves Léglise',
    },
    hosting: {
      title: '托管',
    },
    dataProtection: {
      title: '数据保护',
      description: '有关您个人数据保护的详细信息，请查看我们的隐私政策。',
      link: '阅读我们的隐私政策',
    },
  },
  // 隐私政策页面
  privacyPage: {
    title: '隐私政策',
    subtitle: '我们如何保护您的个人数据',
    lastUpdated: '最后更新：2025年1月1日',
    intro: {
      title: '介绍',
      description: 'KaraoNow 致力于保护您的隐私。本政策解释了我们如何按照 GDPR 收集、使用和保护您的个人数据。',
    },
    dataCollection: {
      title: '收集的数据',
      organizers: {
        title: '对于组织者',
        email: '电子邮件地址',
        name: '名、姓氏和组织',
        organization: '组织详情',
        payment: '付款信息（通过第三方处理器）',
        events: '有关创建事件的信息',
      },
      singers: {
        title: '对于歌手',
        email: '电子邮件地址（可选）',
        pseudo: '用户昵称',
        name: '名和姓氏（可选）',
        participation: '您参加的活动',
        history: '添加到队列的歌曲的历史记录',
      },
    },
    purposes: {
      title: '数据使用用途',
      authentication: '身份验证和帐户管理',
      eventManagement: '事件创建和管理',
      songManagement: '歌曲队列管理',
      communication: '关于您帐户的重要通信',
      billing: '支付处理和结算',
      support: '客户支持和技术支持',
      preferences: '改进您的用户体验',
    },
    legalBasis: {
      title: '处理的法律基础',
      description: '您的数据处理基于您在注册时的同意以及您与 KaraoNow 之间的服务合同的执行。',
    },
    retention: {
      title: '数据保留期',
      active: '活跃数据：在您的帐户保持活跃期间保留',
      inactive: '非活跃数据：在上次登录后 12 个月删除',
      legal: '法律数据：按法律要求保留',
    },
    rights: {
      title: '您的 GDPR 权利',
      description: '根据 GDPR，您拥有以下权利：',
      access: '访问您个人数据的权利',
      rectification: '更正您数据的权利',
      deletion: '被遗忘权（数据删除）',
      portability: '数据可携带权',
      opposition: '反对处理的权利',
      limitation: '限制处理的权利',
      contact: '要行使这些权利，请通过 support@karaonow.com 与我们联系',
    },
    security: {
      title: '安全',
      description: '我们采取适当的安全措施来保护您的数据免受未授权访问、修改或披露。',
    },
    cookies: {
      title: 'Cookie',
      description: 'KaraoNow 使用 Cookie 来改进您的用户体验。有关详细信息，请查看我们的 Cookie 政策。',
      link: '阅读我们的 Cookie 政策',
    },
    contact: {
      title: '联系我们',
      description: '如果您对本政策或您的个人数据有任何疑问，请与我们联系：',
      email: 'support@karaonow.com',
    },
  },
  // 服务条款页面
  termsPage: {
    title: '条款和条件',
    subtitle: 'KaraoNow 条款和条件',
    lastUpdated: '最后更新：2025年1月1日',
    intro: {
      title: '介绍',
      description: '欢迎来到 KaraoNow！通过使用我们的平台，您接受这些条款和条件。请仔细阅读。',
    },
    service: {
      title: '服务描述',
      description: 'KaraoNow 是用于管理卡拉 OK 活动的 SaaS 平台。该平台提供两个主要界面：',
      organizers: {
        title: '对于组织者',
        description: '组织者可以创建事件、生成二维码与歌手共享、管理歌曲队列并修改歌手轮次顺序。订阅系统提供三种计划：免费（有限）、单一事件和高级（每月订阅）。',
      },
      singers: {
        title: '对于歌手',
        description: '歌手可以通过二维码加入活动、将歌曲添加到活动队列并查看歌曲顺序。歌手可以在没有帐户（访客模式）的情况下参与，也可以使用帐户获得高级功能。',
      },
    },
    accounts: {
      title: '用户帐户',
      description: '要使用 KaraoNow，您必须创建帐户。您负责保持凭据的机密性。',
      responsibility: '您对通过您的帐户执行的所有活动负责',
      accuracy: '您同意提供准确且最新的信息',
      security: '您同意保持密码安全',
      notTransferable: '您的帐户是个人的，不可转让',
    },
    subscription: {
      title: '订阅计划',
      description: 'KaraoNow 为组织者提供多种订阅计划：',
      free: '免费计划：最多 4 个活动（所有状态合并，不包括唯一活动），每个活动 15 首歌曲',
      oneShot: '唯一活动：为特定活动一次性付款，无限歌曲，有效期有限',
      premium: '按月订阅的付费计划：提供多种计划，具有月度活动配额，每个活动无限歌曲，自动月度更新',
    },
    payment: {
      title: '付款条件',
      description: '付款通过安全的第三方支付处理器处理。',
      currency: '付款以欧元 (EUR) 进行',
      noRefund: '已提供的服务不退款',
      cancellation: '您可以随时取消订阅',
      automaticRenewal: '月度订阅将在每月自动更新，除非取消',
    },
    conduct: {
      title: '用户行为',
      description: '您同意以合法和道德的方式使用 KaraoNow。',
      prohibited: {
        title: '禁止行为',
        illegal: '任何非法活动或违反适用法律',
        harmful: '有害、冒犯或诽谤性内容',
        harassing: '骚扰、恐吓或歧视',
        spam: '发送垃圾邮件或其他不需要的通信',
        fraud: '欺诈或诈骗企图',
      },
    },
    organizerResponsibilities: {
      title: '组织者的责任',
      description: '组织者接受以下责任：',
      songSelection: '您对歌曲内容的合法性负责',
      eventManagement: '您必须按照这些条款管理您的活动',
      dataManagement: '您负责管理歌手数据',
      copyrightCompliance: '您同意遵守歌曲的著作权法',
    },
    singerRights: {
      title: '歌手的权利和责任',
      description: '歌手拥有以下权利并承担以下责任：',
      participation: '您可以参与他人组织的活动',
      songSelection: '您可以将歌曲添加到活动队列',
      noDuplication: '您不能多次添加同一首歌曲',
      respect: '您同意尊重组织者制定的规则',
    },
    intellectualProperty: {
      title: '知识产权',
      description: 'KaraoNow 及其内容（音乐歌曲除外）的所有知识产权均属于 KaraoNow。未经许可，您不能复制或使用该内容。',
    },
    liability: {
      title: '责任限制',
      description: 'KaraoNow 按"现状"提供。我们不对因平台使用引起的直接或间接损害负责。我们的责任限制为上个月内您为订阅支付的最大金额。',
    },
    termination: {
      title: '终止',
      description: '在违反这些条款的情况下，KaraoNow 保留暂停或终止您的帐户的权利。您可以随时终止您的帐户。',
    },
    modifications: {
      title: '条款修改',
      description: 'KaraoNow 保留随时修改这些条款的权利。修改将在通知后 30 天生效。在该期间之后继续使用平台意味着您接受新条款。',
    },
    jurisdiction: {
      title: '管辖权',
      description: '这些条款受法国法律管辖。任何争议将由法国主管法院处理。',
    },
    contact: {
      title: '联系',
      description: '如果您对这些条款有任何疑问，请与我们联系：',
      email: 'support@karaonow.com',
    },
  },
}
